<script setup lang="ts">
/* A tiny daily-series sparkline for the admin dashboard: line + soft area,
   with an invisible per-day hit strip so hovering shows "day: value". */
import { computed } from 'vue'

const props = defineProps<{
  values: number[]
  days: string[]
  /** Formats a value for the hover title (default: locale number). */
  format?: (value: number) => string
}>()

const W = 100
const H = 28
const PAD = 2

const max = computed(() => Math.max(1, ...props.values))
const step = computed(() => (props.values.length > 1 ? W / (props.values.length - 1) : W))

const points = computed(() =>
  props.values
    .map((v, i) => {
      const x = props.values.length > 1 ? i * step.value : W / 2
      const y = H - PAD - (v / max.value) * (H - PAD * 2)
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' '),
)
const area = computed(() => (props.values.length ? `0,${H} ${points.value} ${W},${H}` : ''))
const fmt = (v: number) => (props.format ? props.format(v) : v.toLocaleString())
</script>

<template>
  <svg class="spark" :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" role="img" :aria-label="`${values.length}-day trend`">
    <polygon :points="area" class="spark-area" />
    <polyline :points="points" class="spark-line" />
    <rect
      v-for="(v, i) in values"
      :key="days[i]"
      class="spark-hit"
      :x="Math.max(0, i * step - step / 2)"
      y="0"
      :width="step"
      :height="H"
    >
      <title>{{ days[i] }}: {{ fmt(v) }}</title>
    </rect>
  </svg>
</template>

<style scoped>
.spark { display: block; width: 100%; height: 32px; overflow: visible; color: var(--accent); }
.spark-line { fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linejoin: round; stroke-linecap: round; vector-effect: non-scaling-stroke; }
.spark-area { fill: currentColor; opacity: 0.12; }
.spark-hit { fill: transparent; }
.spark-hit:hover { fill: color-mix(in srgb, currentColor 14%, transparent); }
</style>
